/**
 * IndexedDB persistence for the image editor's current session.
 * Falls back silently to no-op if IndexedDB is unavailable.
 */

const DB_NAME = 'fias-image-editor';
const STORE_NAME = 'sessions';
const DB_VERSION = 1;
const SESSION_KEY = 'current';

export interface PersistedSession {
  canvasJSON: object;
  fileName: string;
  fileId: string | null;
  filters: { brightness: number; contrast: number; saturation: number };
  /** Fabric viewport transform [a, b, c, d, e, f] — captures zoom + pan. */
  viewportTransform?: number[];
  savedAt: number;
}

interface StoredRecord extends PersistedSession {
  id: string;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function loadSession(): Promise<PersistedSession | null> {
  try {
    const db = await openDb();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(SESSION_KEY);
      request.onsuccess = () => {
        const record = request.result as StoredRecord | undefined;
        if (record && typeof record.canvasJSON === 'object') {
          const { id: _id, ...session } = record;
          resolve(session);
        } else {
          resolve(null);
        }
      };
      request.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

export async function saveSession(session: PersistedSession): Promise<void> {
  try {
    const db = await openDb();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const record: StoredRecord = { id: SESSION_KEY, ...session };
      const request = store.put(record);
      request.onsuccess = () => resolve();
      request.onerror = () => resolve();
    });
  } catch {
    // silently degrade
  }
}

export async function clearSession(): Promise<void> {
  try {
    const db = await openDb();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const request = store.delete(SESSION_KEY);
      request.onsuccess = () => resolve();
      request.onerror = () => resolve();
    });
  } catch {
    // silently degrade
  }
}
